import React from 'react';
import { Button, CopyToClipboard, Tooltip } from '@/components/ui';
import { ColumnDef } from '@tanstack/react-table';
import { IconStack, IconX } from '@tabler/icons-react';
import { ProcessedFile, TableMode } from '../types';

interface FileTableColumnsProps {
  tableMode: TableMode;
  onRemoveFile?: (file: ProcessedFile) => void;
  onShowDuplicatesOf?: (fileName: string) => void;
  showDuplicatesOf?: string | null;
  getUploadStatus?: (file: File) => {
    status: string;
    progress: number;
    error?: string;
  } | null;
}

export const createFileTableColumns = ({
  tableMode,
  onRemoveFile,
  onShowDuplicatesOf,
  showDuplicatesOf,
  getUploadStatus,
}: FileTableColumnsProps): ColumnDef<ProcessedFile>[] => [
  // Index column
  {
    id: 'index',
    header: '#',
    cell: ({ row }) => (
      <div className="bulk-file-table__index">{row.index + 1}</div>
    ),
    enableSorting: false,
    enableColumnFilter: false,
    meta: {
      size: 'auto',
      systemColumn: true,
    },
  },

  // File name column
  {
    accessorKey: 'file.name',
    header: 'File name',
    filterFn: 'includesString',
    meta: {
      size: '2fr',
      required: true,
      cell: {
        variant: 'fileName',
        showIcon: true,
        truncate: true,
        className: (row: ProcessedFile) =>
          row.processedStatus === 'duplicated'
            ? 'bulk-file-table__file-name-text--duplicate'
            : '',
        endSlot: (row: ProcessedFile) => {
          const isDuplicate = row.processedStatus === 'duplicated';
          if (isDuplicate && !showDuplicatesOf && onShowDuplicatesOf) {
            return (
              <Button
                variant="outlined"
                ml="4"
                size="xsmall"
                startIcon={<IconStack />}
                onClick={() => onShowDuplicatesOf(row.file.name)}
                className="bulk-file-table__show-duplicates-btn"
              >
                Group Duplicates
              </Button>
            );
          }
          return null;
        },
      },
    },
  },

  // Extension column
  {
    id: 'extension',
    header: 'Extension',
    accessorFn: row => {
      const fileName = row.file.name;
      return fileName.split('.').pop()?.toLowerCase() || '';
    },
    filterFn: 'includesString',
    meta: {
      cell: {
        variant: 'fileFormat',
      },
      width: '120px',
    },
  },

  // Path column
  {
    id: 'path',
    header: 'Path',
    cell: ({ row }) => {
      const fullPath = row.original.file.webkitRelativePath || 'No path';

      // Extract directory path (remove filename)
      const directoryPath =
        fullPath === 'No path'
          ? 'No path'
          : (() => {
              const pathParts = fullPath.split('/');
              pathParts.pop(); // Remove filename
              return pathParts.length > 0 ? pathParts.join('/') : 'Root';
            })();

      return (
        <div className="bulk-file-table__file-path">
          <Tooltip content={fullPath}>
            <CopyToClipboard
              value={fullPath}
              successMessage="Path copied to clipboard"
            >
              {directoryPath}
            </CopyToClipboard>
          </Tooltip>
        </div>
      );
    },
    accessorFn: row => row.file.webkitRelativePath || '',
    filterFn: 'includesString',
  },

  // Size column
  {
    id: 'size',
    header: 'Size',
    accessorFn: row => row.file.size,
    sortingFn: 'basic',
    meta: {
      cell: {
        variant: 'fileSize',
      },
      width: '100px',
    },
  },

  // Status column
  {
    id: 'status',
    header: 'Status',
    accessorFn: row => row.processedStatus,
    filterFn: 'equals',
    meta: {
      cell: {
        variant: 'status',
        entityType: tableMode === TableMode.UPLOAD_PROGRESS ? 'upload' : 'file',
        status: (row: ProcessedFile) => row.processedStatus,
        label: (row: ProcessedFile) => {
          // Handle uploading with progress - show custom label with percentage
          if (
            tableMode === TableMode.UPLOAD_PROGRESS &&
            getUploadStatus &&
            row.processedStatus === 'uploading'
          ) {
            const uploadStatus = getUploadStatus(row.file);
            if (uploadStatus && uploadStatus.status === 'uploading') {
              return `Uploading ${Math.round(uploadStatus.progress)}%`;
            }
          }
          return undefined; // Use default label from status mapping
        },
        size: 'small',
      },
      minSize: '200px',
      maxSize: 'auto',
      required: true,
    },
  },

  // Message column
  {
    id: 'message',
    header: 'Message',
    accessorFn: row => {
      if (row.isDuplicate && row.duplicateMessage) return row.duplicateMessage;
      if (row.validationStatus.errors?.[0])
        return row.validationStatus.errors[0];
      return row.validationStatus.message || '';
    },
    filterFn: 'includesString',
    meta: {
      cell: {
        variant: 'message',
      },
      minSize: '200px',
      maxSize: '1fr',
    },
  },

  // Actions column
  {
    id: 'actions',
    header: () => null,
    cell: ({ row }) => {
      const file = row.original;

      if (!onRemoveFile) return null;

      return (
        <div className="bulk-file-table__actions">
          <Button
            variant="ghost"
            size="xsmall"
            color="error"
            isIconOnly
            onClick={() => onRemoveFile(file)}
            startIcon={<IconX />}
            title="Remove file"
          />
        </div>
      );
    },
    enableSorting: false,
    enableHiding: false,
    enableColumnFilter: false,
    meta: {
      width: 'auto',
      systemColumn: true,
    },
  },
];
